<script lang="ts">
	import { page } from '$app/stores';
	import { managementTools } from '$lib/links.js';
	import { Home, ChevronRight, ChevronUp, ChevronLeft } from '@lucide/svelte';
	import { onMount, afterUpdate } from 'svelte';

	const TIME_MANAGEMENT_BASE_PATH = '/management';
	let breadcrumbs: { name: string; href?: string }[] = [];
	
	// Go to top button state
	let showGoToTop = false;

	// Desktop sidebar state
	let desktopSidebarOpen = true;

	function formatSegmentName(segment: string): string {
		return segment
			.split('-')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	// Get page title for sharing
	$: pageTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.name || 'Management Tool' : 'Management Tool';

	$: {
		const currentRelativePath = $page.url.pathname.startsWith(TIME_MANAGEMENT_BASE_PATH)
			? $page.url.pathname.substring(TIME_MANAGEMENT_BASE_PATH.length)
			: '';
		const currentSegments = currentRelativePath.split('/').filter(s => s);

		breadcrumbs = [
			{ name: 'Home', href: '/' },
			{ name: 'Management', href: TIME_MANAGEMENT_BASE_PATH }
		];

		let currentHref = TIME_MANAGEMENT_BASE_PATH;
		currentSegments.forEach((segment, index) => {
			currentHref += `/${segment}`;
			const name = formatSegmentName(segment);

			if (name) {
				breadcrumbs.push({
					name,
					href: index < currentSegments.length - 1 ? currentHref : undefined
				});
			}
		});
	}

	// Load AddToAny script with better timing
	function loadAddToAnyScript() {
		if (typeof window !== 'undefined' && !(window as any).a2a) {
			const script = document.createElement('script');
			script.async = true;
			script.src = 'https://static.addtoany.com/menu/page.js';
			script.onload = () => {
				// Reinitialize AddToAny after script loads
				if ((window as any).a2a) {
					(window as any).a2a.init_all();
				}
			};
			document.head.appendChild(script);
		} else if ((window as any).a2a) {
			// Reinitialize if script already exists
			(window as any).a2a.init_all();
		}
	}

	// Handle scroll for go-to-top button
	function handleScroll() {
		if (typeof window !== 'undefined') {
			// Use the same detection method as the working scroll function
			let maxScroll = 0;
			
			// Check standard scroll positions
			maxScroll = Math.max(maxScroll, window.pageYOffset);
			maxScroll = Math.max(maxScroll, document.documentElement.scrollTop);
			maxScroll = Math.max(maxScroll, document.body.scrollTop);
			
			const mainElement = document.querySelector('main');
			if (mainElement) {
				maxScroll = Math.max(maxScroll, mainElement.scrollTop);
			}
			
			// Check all elements with overflow scroll/auto that have scroll > 0
			const allElements = document.querySelectorAll('*');
			allElements.forEach((el) => {
				const styles = window.getComputedStyle(el);
				if (styles.overflowY === 'scroll' || styles.overflowY === 'auto') {
					maxScroll = Math.max(maxScroll, (el as HTMLElement).scrollTop);
				}
			});
			
			// Show button when any scroll position > 100px
			showGoToTop = maxScroll > 100;
		}
	}

	// Find and scroll the actual scrollable element
	function scrollToTop() {
		console.log('=== SCROLLING TO TOP ===');
		
		// Always try all scroll methods - don't rely on finding current scroll position
		let scrolled = false;
		
		// Method 1: Window scroll
		if (window.pageYOffset > 0 || document.documentElement.scrollTop > 0 || document.body.scrollTop > 0) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			document.documentElement.scrollTop = 0;
			document.body.scrollTop = 0;
			console.log('Scrolled window');
			scrolled = true;
		}
		
		// Method 2: Main element scroll
		const mainElement = document.querySelector('main');
		if (mainElement && mainElement.scrollTop > 0) {
			mainElement.scrollTo({ top: 0, behavior: 'smooth' });
			console.log('Scrolled main element');
			scrolled = true;
		}
		
		// Method 3: Check ALL scrollable elements (even if at bottom)
		const allElements = document.querySelectorAll('*');
		allElements.forEach((el, index) => {
			const styles = window.getComputedStyle(el);
			if (styles.overflowY === 'scroll' || styles.overflowY === 'auto') {
				const element = el as HTMLElement;
				// Check if element is scrollable (has content that can scroll)
				if (element.scrollHeight > element.clientHeight) {
					element.scrollTo({ top: 0, behavior: 'smooth' });
					element.scrollTop = 0;
					console.log(`Scrolled element ${index}:`, el.tagName, el.className);
					scrolled = true;
				}
			}
		});
		
		if (!scrolled) {
			console.log('No scrollable elements found, forcing window scroll');
			// Force scroll anyway
			window.scrollTo(0, 0);
			document.documentElement.scrollTop = 0;
		}
	}

	// Toggle desktop sidebar
	function toggleDesktopSidebar() {
		desktopSidebarOpen = !desktopSidebarOpen;
	}

	onMount(() => {
		loadAddToAnyScript();
		
		// Add scroll listeners to both window and main element
		if (typeof window !== 'undefined') {
			window.addEventListener('scroll', handleScroll);
			
			const mainElement = document.querySelector('main');
			if (mainElement) {
				mainElement.addEventListener('scroll', handleScroll);
			}
			
			return () => {
				window.removeEventListener('scroll', handleScroll);
				if (mainElement) {
					mainElement.removeEventListener('scroll', handleScroll);
				}
			};
		}
	});

	afterUpdate(() => {
		// Reinitialize AddToAny after page navigation
		if (typeof window !== 'undefined' && (window as any).a2a) {
			setTimeout(() => {
				(window as any).a2a.init_all();
			}, 100);
		}
	});
</script>

{#if $page.route.id?.startsWith('/management/to-do-list') || $page.route.id?.startsWith('/management/event-countdown-generator/event')}
	<!-- Todo and event pages have their own layouts, just render the slot -->
	<slot />
{:else}
	<!-- Use the standard layout with AddToAny for other management routes -->
	
	<!-- Desktop Toggle Button -->
	<button
		on:click={toggleDesktopSidebar}
		class="hidden md:flex fixed top-2 z-50 bg-white p-2 rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-all duration-300 ease-in-out items-center justify-center"
		style="left: {desktopSidebarOpen ? 'calc(16.666667% + 8px)' : '16px'};"
		aria-label="Toggle sidebar"
	>
		{#if desktopSidebarOpen}
			<ChevronLeft size={20} class="text-gray-600" />
		{:else}
			<ChevronRight size={20} class="text-gray-600" />
		{/if}
	</button>

	<div class="flex flex-grow w-full">
		<aside class="hidden md:block bg-gray-50 border-r border-gray-200 p-4 md:sticky md:top-0 md:h-screen md:overflow-y-auto transition-all duration-300 ease-in-out {desktopSidebarOpen ? 'md:w-1/6' : 'md:w-0 md:p-0 md:border-0'}">
			{#if desktopSidebarOpen}
				<h2 class="text-lg font-semibold text-gray-800 mb-4">Management Tools</h2>
				<ul class="space-y-2">
					{#each managementTools as tool (tool.href)}
						<li>
							<a
								href={tool.href}
								class="block p-3 rounded-lg transition-colors duration-150"
								class:bg-blue-100={$page.url.pathname === tool.href}
								class:text-blue-700={$page.url.pathname === tool.href}
								class:font-medium={$page.url.pathname === tool.href}
								class:hover:bg-gray-100={$page.url.pathname !== tool.href}
								class:text-gray-600={$page.url.pathname !== tool.href}
								class:hover:text-gray-900={$page.url.pathname !== tool.href}
							>
								<div class="font-medium text-sm">{tool.text}</div>
								<div class="text-xs mt-1 opacity-80">{tool.description}</div>
							</a>
						</li>
					{/each}
				</ul>
			{/if}
		</aside>

		<main class="w-full p-4 md:h-screen md:overflow-y-auto transition-all duration-300 ease-in-out {desktopSidebarOpen ? 'md:w-5/6' : 'md:w-full'}">
			<!-- Breadcrumbs with Share Buttons -->
			<div class="flex justify-between items-center mb-4">
				<nav aria-label="Breadcrumb">
					<ol class="flex items-center space-x-1 text-sm text-gray-500 flex-wrap">
						{#each breadcrumbs as breadcrumb, i (i)}
							<li class="flex items-center">
								{#if i > 0}
									<ChevronRight size={16} class="flex-shrink-0 text-gray-400 mx-1" />
								{/if}
								{#if breadcrumb.href}
									<a href={breadcrumb.href} class="hover:text-gray-700 hover:underline">
										{#if i === 0} <Home size={14} class="inline mr-1 align-text-bottom"/> {/if}
										{breadcrumb.name}
									</a>
								{:else}
									<span class="font-medium text-gray-600">{breadcrumb.name}</span>
								{/if}
							</li>
						{/each}
					</ol>
				</nav>
				
				<!-- AddToAny Share Buttons -->
				<div class="a2a_kit a2a_kit_size_32 a2a_default_style" data-a2a-url="" data-a2a-title="{pageTitle} - Management Tool">
					<a class="a2a_dd" href="https://www.addtoany.com/share"></a>
					<a class="a2a_button_facebook"></a>
					<a class="a2a_button_twitter"></a>
					<a class="a2a_button_linkedin"></a>
					<a class="a2a_button_reddit"></a>
					<a class="a2a_button_telegram"></a>
					<a class="a2a_button_whatsapp"></a>
					<a class="a2a_button_copy_link"></a>
				</div>
			</div>

			<slot />

			<!-- Go to Top Button -->
			<button
				on:click={scrollToTop}
				class="fixed bottom-6 right-8 bg-pink-600 hover:bg-pink-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out opacity-80 hover:opacity-100 z-50"
				aria-label="Go to top"
				title="Go to top"
			>
				<ChevronUp size={16} />
			</button>

			<!-- Mobile Navigation -->
			<aside class="block md:hidden w-full bg-gray-50 border-t border-gray-200 p-4 mt-6">
				<h2 class="text-lg font-semibold text-gray-800 mb-4">Management Tools</h2>
				<ul class="space-y-2">
					{#each managementTools as tool (tool.href)}
						<li>
							<a
								href={tool.href}
								class="block p-3 rounded-lg transition-colors duration-150"
								class:bg-blue-100={$page.url.pathname === tool.href}
								class:text-blue-700={$page.url.pathname === tool.href}
								class:font-medium={$page.url.pathname === tool.href}
								class:hover:bg-gray-100={$page.url.pathname !== tool.href}
								class:text-gray-600={$page.url.pathname !== tool.href}
								class:hover:text-gray-900={$page.url.pathname !== tool.href}
							>
								<div class="font-medium text-sm">{tool.text}</div>
								<div class="text-xs mt-1 opacity-80">{tool.description}</div>
							</a>
						</li>
					{/each}
				</ul>
			</aside>
		</main>
	</div>
{/if} 